import {find as _find, has as _has} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {withRouter} from 'react-router';
import {compose} from 'redux';
import {Header} from 'semantic-ui-react';

import {gql, graphql} from 'react-apollo';
import {renderModalError} from '@utils/forms';
import {showModal} from '@utils/modal';
import {convertToInt, isUrlParamValid} from '@utils/helpers';
import {getLink} from '@appComponents/Link';
import {routes} from '@constants/routes';
import {
    CONTACT_PERSON_TYPE_CLIENT_CONTACT,
    CONTACT_PERSON_TYPE_CLIENT_KEY_ACCOUNT_MANAGER,
    CONTACT_PERSON_TYPE_CLIENT_SALES_REPRESENTATIVE,
} from '@constants/variables';
import {ButtonAdd} from '@appComponents/ButtonCollection';
import {contactPersonIndex, contactPersonTypes} from '@graphql/clients/contactPerson';

import ContactPersonsTable from '../components/ContactPersonsTable';
import ContactPersonForm from '../forms/ContactPersonForm';
import {CONTACT_PERSON_INDEX_BUTTONS_ADD} from '../constants/messages';

export class ContactPersonsIndex extends React.Component {
    static propTypes = {
        clientId: PropTypes.number.isRequired,
        DataContactPersons: PropTypes.object.isRequired,
        DataContactPersonTypes: PropTypes.object.isRequired,
        match: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.state = {
            modalOpen: false,
            contactPersons: null,
            contactPersonTypes: null,
        };
    }

    componentDidMount() {
        if (!isUrlParamValid(this.props.match.params.id)) {
            return showModal({
                isVisible: true,
                content: renderModalError('Client', routes.clients.index.path),
            });
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            DataContactPersons: {contactPersons},
            DataContactPersonTypes: {contactPersonTypes},
        } = nextProps;

        const nextState = {};

        if (contactPersonTypes && !prevState.contactPersonTypes) {
            nextState.contactPersonTypes = contactPersonTypes.map((type) => ({
                key: type.value,
                text: type.text,
                value: type.value,
            }));
        }

        if (contactPersons && contactPersons !== prevState.contactPersons) {
            nextState.contactPersons = contactPersons;
        }

        return Object.keys(nextState).length ? nextState : null;
    }

    componentDidUpdate(prevProps, prevState) {
        const {match} = this.props;
        const {contactPersons, modalOpen} = this.state;
        const contactPersonsRoutes = routes.clients.contactPersons;

        if (modalOpen) return;

        if (match.path === contactPersonsRoutes.add.path) {
            this.openModal({});

            return;
        }

        if (
            match.path === contactPersonsRoutes.edit.path
            && contactPersons
            && contactPersons !== prevState.contactPersons
        ) {
            const contactPerson = contactPersons.find(item => convertToInt(item.id) === convertToInt(match.params.contactPersonId));

            contactPerson
                ? this.openModal(contactPerson)
                : showModal({
                    isVisible: true,
                    content: renderModalError('Contact person', getLink('clients.contactPersons.index', {id: match.params.id})),
                });
        }
    }

    resetIndexUrl = () => {
        window.history.pushState({}, '', getLink('clients.contactPersons.index', {id: this.props.clientId}));
    };

    openModal = (id) => {
        const {match, clientId} = this.props;
        let data, modalLink, error = false;

        if (!isNaN(id)) {
            data = _find(this.state.contactPersons, {id});
            modalLink = getLink('clients.contactPersons.edit', {contactPersonId: id, id: clientId});
        } else if (_has(id, 'id')
            && isUrlParamValid(match.params.id)
            && isUrlParamValid(match.params.contactPersonId)) {

            data = id;
            modalLink = getLink('clients.contactPersons.edit', {
                contactPersonId: id.id,
                id: clientId,
            });
        } else if (!match.params.contactPersonId && isUrlParamValid(match.params.id)) {
            data = {};
            modalLink = getLink('clients.contactPersons.add', {id: clientId});
        } else if (!isUrlParamValid(match.params.id)) {
            return;
        } else {
            modalLink = getLink('clients.contactPersons.edit', {
                contactPersonId: match.params.contactPersonId,
                id: clientId,
            });
            error = true;
        }

        window.history.pushState({}, '', modalLink);

        const content = error
            ? renderModalError('Contact person', getLink('clients.contactPersons.index', {id: clientId}))
            : <ContactPersonForm
                clientId={clientId}
                formData={{
                    contactPerson: data,
                    contactPersonTypes: this.state.contactPersonTypes,
                }}
                resetIndexUrl={this.resetIndexUrl}
            />;

        showModal({
            isVisible: true,
            content: content,
            onClose: this.resetIndexUrl,
        });

        this.setState({modalOpen: true});
    };

    render() {
        const {contactPersons} = this.state;
        const {DataContactPersons} = this.props;

        return (
            <div>
                <Header>
                    <div className='indexButtons'>
                        <ButtonAdd onClick={this.openModal}>
                            {CONTACT_PERSON_INDEX_BUTTONS_ADD}
                        </ButtonAdd>
                    </div>
                </Header>
                <ContactPersonsTable
                    contactPersons={contactPersons}
                    editClick={this.openModal}
                    loading={DataContactPersons.loading}
                />
            </div>
        );
    }
}

const ContactPersonsQuery = gql(contactPersonIndex);
const ContactPersonTypesQuery = gql(contactPersonTypes);

const ContactPersonsWithGraphQL = compose(
    graphql(ContactPersonsQuery, {
        options: (props) => ({
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            variables: {clientId: props.clientId},
        }),
        name: 'DataContactPersons',
    }),
    graphql(ContactPersonTypesQuery, {
        options: () => ({
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
            variables: {
                id: [
                    CONTACT_PERSON_TYPE_CLIENT_CONTACT,
                    CONTACT_PERSON_TYPE_CLIENT_KEY_ACCOUNT_MANAGER,
                    CONTACT_PERSON_TYPE_CLIENT_SALES_REPRESENTATIVE,
                ],
            },
        }),
        name: 'DataContactPersonTypes',
    })
)(ContactPersonsIndex);

export default withRouter((ContactPersonsWithGraphQL));
